import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import keycloak from '../keycloak';

const API_ROOT = process.env.REACT_APP_API_ROOT || '/api';

// Then use API_ROOT as defined earlier

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: API_ROOT }),
  tagTypes: ['Risk'],
  endpoints: build => ({
    getRiskForAddress: build.query({
      query: ({ address, coordinates }) => ({
        url: '/property/risks',
        method: 'POST',
        body: {
          address,
          coordinates,
        },
      }),
      providesTags: ['Risk'],
    }),
    getRiskForId: build.query({
      query: ({ id }) => ({
        url: '/property/risks-by-id',
        method: 'POST',
        body: {
          id,
        },
      }),
      providesTags: ['Risk'],
    }),
    getNeighborhoodProfile: build.query({
      query: ({ bbox }) => ({
        url: '/property/neighborhood-profile',
        method: 'POST',
        body: {
          bbox,
        },
      }),
    }),
    submitElevationCertificate: build.mutation({
      query: formData => ({
        url: '/admin/elevation-certificate',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: { ...formData },
      }),
    }),
    uploadElevationCertificate: build.mutation({
      query: ({ fileData, coordinates }) => {
        const formData = new FormData();
        formData.append('file', fileData);
        formData.append('coordinates', coordinates);
        return {
          url: '/property/upload-elevation-certificate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Risk'],
    }),
  }),
});

export const downloadReport = async ({ address, coordinates }) => {
  try {
    const reportFetch = await fetch(`${API_ROOT}/property/report`, {
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        address,
        coordinates,
      }),
      method: 'POST',
    });

    if (!reportFetch.ok) {
      throw new Error(`HTTP error! status: ${reportFetch.status}`);
    }

    const blob = await reportFetch.blob();
    const dataUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = dataUrl;
    downloadLink.download = 'report.pdf';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(dataUrl); // clean up memory
  } catch (error) {
    toast.error('Oops! We are unable to create a report for this address, please try again later.');
    console.error(error);
  }
};

// These export hooks are automatically defined by createApi, they
// can be imported in functional components to fetch data, see fetch
// status and get the data from the redux store.
export const {
  useGetNeighborhoodProfileQuery,
  useGetRiskForAddressQuery,
  useGetRiskForIdQuery,
  useLazyGetNeighborhoodProfileQuery,
  useSubmitElevationCertificateMutation,
  useUploadElevationCertificateMutation,
} = api;
